import { Injectable } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { Router } from "@angular/router";
import { ErrorHandlingService } from './error-handling.service';
import { StandardHttpError } from '../interface/http-error.interface';
import { SKIP_ERROR_HANDLING } from '../interface/http-context.interface';
import { OAUTH_CONSTANTS } from '../../../auth/constants/oauth.constants';

const AUTH_PAGES = [OAUTH_CONSTANTS.OAUTH_REDIRECT_ROUTE, OAUTH_CONSTANTS.OAUTH_LOGOUT_ROUTE];

/**
 * Interceptor HTTP para manejo centralizado de requests y errores
 *
 * @remarks
 * - Agrega token JWT y x-interaction-id a todos los requests
 * - Maneja errores HTTP de forma centralizada con códigos ER-XXX
 * - Redirige a login en caso de 401 (sesión expirada)
 * - Muestra modales de error automáticamente (excepto 401)
 *
 * @example
 * ```typescript
 * // Configurado en AppModule providers
 * { provide: HTTP_INTERCEPTORS, useClass: InterceptorService, multi: true }
 * ```
 */
@Injectable({
  providedIn: "root",
})
export class InterceptorService implements HttpInterceptor {
  constructor(
    private readonly router: Router,
    private readonly errorHandlingService: ErrorHandlingService
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const token = sessionStorage.getItem("token");
    const reqInteraction = crypto.randomUUID();
    let request = req;

    if (token) {
      request = req.clone({
        setHeaders: {
          authorization: `Bearer ${token}`,
          'x-interaction-id': reqInteraction
        },
      });
    } else {
      request = req.clone({
        setHeaders: {
          'x-interaction-id': reqInteraction
        },
      });
    }

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        // Procesar error HTTP (intenta extraer del backend, fallback a local)
        const standardError: StandardHttpError = this.errorHandlingService.processHttpError(
          err,
          reqInteraction
        );

        // Manejo especial para 401: redirigir a OAuth2 sin mostrar modal
        if (err.status === 401) {
          const returnUrl = this.router.url;

          // Evitar bucles si ya estás en una página de auth
          if (!AUTH_PAGES.some(page => returnUrl.includes(page))) {
            this.router.navigate([OAUTH_CONSTANTS.OAUTH_REDIRECT_ROUTE], {
              queryParams: { returnUrl },
            });
          }

          // No mostrar modal para 401, solo redirigir
          return throwError(() => standardError);
        }

        // Verificar si el cliente solicitó omitir el manejo automático de errores
        const skipErrorHandling = request.context.get(SKIP_ERROR_HANDLING);

        if (skipErrorHandling) {
          // El service manejará el error explicitamente, no mostrar modal
          console.warn('[InterceptorService] Skip error handling activado para:', request.url);
          return throwError(() => standardError);
        }

        // Para otros errores, mostrar modal automáticamente
        this.errorHandlingService.displayError(standardError).catch((modalError: unknown) => {
          console.error('[InterceptorService] Error mostrando modal:', modalError);
        });

        return throwError(() => standardError);
      })
    );
  }
}
